import { DatePipe } from '@angular/common'
import {
  AmazonConnectIntegrationComponent,
  AmazonConnectIntegrationData,
  AmazonConnectIntegrationDialogResult,
} from './amazon-connect-integration/amazon-connect-integration.component'
import {
  InboundApiKeyManagementComponent,
  InboundApiKeyManagementData,
} from './amazon-connect-integration/api-key-management/inbound-api-key-management.component'
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Title } from '@angular/platform-browser'

import { MatIcon } from '@angular/material/icon'
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTableModule } from '@angular/material/table'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { Observable, forkJoin, of } from 'rxjs'
import { tap, switchMap, catchError, finalize } from 'rxjs/operators'

import { LoadingComponent } from '../../../components/loading/loading.component'
import { ButtonComponent } from '../../../components/button/button.component'

import {
  AmptalkIntegrationComponent,
  AmptalkIntegrationData,
} from './amptalk-integration/amptalk-integration.component'
import {
  BlueBeanIntegrationComponent,
  BlueBeanIntegrationData,
  BlueBeanIntegrationResult,
} from './bluebean-integration/bluebean-integration.component'
import { BlueBeanOperatorMappingDialogComponent } from './bluebean-integration/operator-mapping/bluebean-operator-mapping-dialog.component'
import {
  KaikuraIntegrationComponent,
  KaikuraIntegrationData,
  KaikuraIntegrationResult,
} from './kaikura-integration/kaikura-integration.component'
import {
  BiztelIntegrationComponent,
  BiztelIntegrationData,
  BiztelIntegrationResult,
} from './biztel-integration/biztel-integration.component'
import { BiztelAccountMappingDialogComponent } from './biztel-integration/account-mapping/biztel-account-mapping-dialog.component'
import { BiztelPhoneNumberTagMappingDialogComponent } from './biztel-integration/phone-number-tag-mapping/biztel-phone-number-tag-mapping-dialog.component'
import { IntegrationSuccessComponent } from './messages/integration-success/integration-success.component'
import { IntegrationResetWarningComponent } from './messages/integration-reset-warning/integration-reset-warning.component'
import { SalesforceConnectSuccessComponent } from './messages/salesforce-connect-success/salesforce-connect-success.component'
import {
  SalesforceIntegrationValidationErrorComponent,
  SalesforceIntegrationValidationErrorParam,
} from './messages/salesforce-integration-validation-error/salesforce-integration-validation-error.component'
import { SalesforceIntegrationValidationSuccessComponent } from './messages/salesforce-integration-validation-success/salesforce-integration-validation-success.component'
import { AmazonConnectDisconnectWarningComponent } from './messages/amazon-connect-disconnect-warning/amazon-connect-disconnect-warning.component'
import {
  AmazonConnectOperationError,
  ExternalIntegrationService,
} from '../../../services/external-integration/external-integration.service'
import { SalesforceAuthService } from '../../../services/external-integration/salesforce-integration/salesforce-auth.service'

import { getTitle } from '../../../util/accessibility'
import { environment } from '../../../../environments/environment'
import {
  InboundConnectService,
  SecretKeyType,
  OutboundConnectService,
} from '../../../../_generated/graphql'
import {
  InboundSettingDialogParam,
  InboundSettingDialogComponent,
} from './inbound-setting-dialog/inbound-setting-dialog.component'
import { OutboundSettingDialogComponent } from './outbound-setting-dialog/outbound-setting-dialog.component'
import { UserStore } from '../../../stores/user.store'
import { FormsModule } from '@angular/forms'
import { captureException } from '../../../util/captureException'
import {
  AmazonConnectIntegrationSetting,
  KaikuraIntegrationSetting,
  BiztelIntegrationSetting,
} from '../../../services/external-integration/external-integration.mapping'
import { BlueBeanIntegrationSettingView } from '../../../services/external-integration/external-integration.service'
import { isFeatureAllowed } from '../../../config/feature-permission.config'

@UntilDestroy()
@Component({
  selector: 'app-external-integration-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    MatIcon,
    MatSlideToggle,
    MatTableModule,
    ButtonComponent,
    LoadingComponent,
  ],
  templateUrl: './external-integration-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './external-integration-page.component.scss',
})
export class ExternalIntegrationPageComponent implements OnInit, OnDestroy {
  isAmazonConnectFeatureEnabled = false
  isAmazonConnectIntegrationEnabled = false
  amazonConnectSetting: AmazonConnectIntegrationSetting | null = null
  amazonConnectError: string | null = null
  isAmazonConnectProcessing = false
  hasAmazonConnectSettingLoaded = false
  hasAmazonConnectDisconnected = false
  isAmptalkIntegrationEnabled = false
  hasAmptalkIntegrationError = false
  isKaikuraIntegrationEnabled = false
  isZoomIntegrationEnabled = false
  hasZoomIntegrationError = false
  isSalesforceIntegrationEnabled = false
  hasSalesforceIntegrationError = false
  alreadyValidatedSalesforceIntegration = false
  isBlueBeanFeatureEnabled = false
  isBlueBeanIntegrationEnabled = false
  isBiztelFeatureEnabled = false
  isBiztelIntegrationEnabled = false

  isLoading = false

  amptalkSetting: {
    apiKey: string
    authKey: string
  } = {
    apiKey: '',
    authKey: '',
  }
  kaikuraSetting: KaikuraIntegrationSetting | null = null
  blueBeanSetting: BlueBeanIntegrationSettingView | null = null
  biztelSetting: BiztelIntegrationSetting | null = null

  get canSeeKaikuraIntegration(): boolean {
    return isFeatureAllowed(
      'KAIKURA_INTEGRATION',
      this.userStore.ctxUser()?.enabledFeatures,
    )
  }

  constructor(
    private userStore: UserStore,
    private titleService: Title,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private externalIntegrationService: ExternalIntegrationService,
    private salesforceAuthService: SalesforceAuthService,
  ) {
    this.titleService.setTitle(getTitle('外部連携'))
  }

  ngOnInit() {
    this.isLoading = true

    this.isBlueBeanFeatureEnabled = isFeatureAllowed(
      'BLUE_BEAN_INTEGRATION',
      this.userStore.ctxUser()?.enabledFeatures,
    )

    this.isBiztelFeatureEnabled = isFeatureAllowed(
      'BIZTEL_INTEGRATION',
      this.userStore.ctxUser()?.enabledFeatures,
    )

    this.isAmazonConnectFeatureEnabled = isFeatureAllowed(
      'AMAZON_CONNECT_INTEGRATION',
      this.userStore.ctxUser()?.enabledFeatures,
    )
    const amazonConnectIntegration$ = this.isAmazonConnectFeatureEnabled
      ? this.fetchAmazonConnectIntegrationSetting()
      : of(null)

    const amptalkKeys$ = this.externalIntegrationService.getAmptalkKeys().pipe(
      tap((keys) => {
        this.amptalkSetting = keys
        this.isAmptalkIntegrationEnabled = !!keys.apiKey || !!keys.authKey
      }),
      catchError((error) => {
        console.error('Failed to get Amptalk keys:', error)
        captureException(error)
        return of(null)
      }),
    )

    const zoomIntegration$ = this.externalIntegrationService
      .checkSecretKeyExistence(SecretKeyType.ZoomRefreshToken)
      .pipe(
        tap((result) => {
          this.isZoomIntegrationEnabled = result
        }),
        catchError((error) => {
          console.error('Failed to check Zoom secret key existence:', error)
          captureException(error)
          return of(false)
        }),
      )

    const zoomAutoAssessmentSetting$ = this.externalIntegrationService
      .getAutoAssessmentSetting(InboundConnectService.Zoom)
      .pipe(
        catchError((error) => {
          console.error('Failed to get Zoom autoAssessmentSetting:', error)
          captureException(error)
          return of(null)
        }),
      )

    const amptalkAutoAssessmentSetting$ = this.externalIntegrationService
      .getAutoAssessmentSetting(InboundConnectService.AmpTalk)
      .pipe(
        catchError((error) => {
          console.error('Failed to get Amptalk autoAssessmentSetting:', error)
          captureException(error)
          return of(null)
        }),
      )

    const kaikuraIntegration$ = this.externalIntegrationService
      .getKaikuraIntegrationSetting()
      .pipe(
        tap((setting) => {
          this.kaikuraSetting = setting
          this.isKaikuraIntegrationEnabled = setting?.active === true
        }),
        catchError((error) => {
          console.error('Failed to get Kaikura integration setting:', error)
          captureException(error)
          return of(null)
        }),
      )

    const blueBeanIntegration$ = this.isBlueBeanFeatureEnabled
      ? this.externalIntegrationService.getBlueBeanIntegrationSetting().pipe(
          tap((setting) => {
            this.blueBeanSetting = setting
            this.isBlueBeanIntegrationEnabled = setting?.active === true
          }),
          catchError((error) => {
            console.error('Failed to get BlueBean integration setting:', error)
            captureException(error)
            return of(null)
          }),
        )
      : of(null)

    const biztelIntegration$ = this.isBiztelFeatureEnabled
      ? this.externalIntegrationService.getBiztelIntegrationSetting().pipe(
          tap((setting) => {
            this.biztelSetting = setting
            this.isBiztelIntegrationEnabled = setting?.active === true
          }),
          catchError((error) => {
            console.error('Failed to get Biztel integration setting:', error)
            captureException(error)
            return of(null)
          }),
        )
      : of(null)

    const salesforceIntegration$ = forkJoin([
      this.externalIntegrationService
        .checkSecretKeyExistence(SecretKeyType.SalesforceRefreshToken)
        .pipe(
          tap((result) => {
            this.isSalesforceIntegrationEnabled = result
            if (result) {
              this.alreadyValidatedSalesforceIntegration = true
            }
          }),
          catchError((error) => {
            console.error(
              'Failed to check Salesforce secret key existence:',
              error,
            )
            captureException(error)
            return of(false)
          }),
        ),
      this.externalIntegrationService.getOutboundIntegrationSetting(
        OutboundConnectService.Salesforce,
      ),
    ]).pipe(
      tap(([result, setting]) => {
        this.hasSalesforceIntegrationError = Boolean(!result && setting)
      }),
    )

    forkJoin([
      zoomIntegration$,
      zoomAutoAssessmentSetting$,
      amptalkAutoAssessmentSetting$,
      amptalkKeys$,
      kaikuraIntegration$,
      blueBeanIntegration$,
      biztelIntegration$,
      salesforceIntegration$,
      amazonConnectIntegration$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ([, zoomAutoAssessmentSetting, amptalkAutoAssessmentSetting]) => {
          this.isLoading = false
          // Zoom: 自動停止後も SecretKey が残りトグルが ON のままになるので、明示的にトグル OFF + アラート。
          if (zoomAutoAssessmentSetting?.active === false) {
            this.isZoomIntegrationEnabled = false
            this.hasZoomIntegrationError = true
          }
          // Amptalk: キーは残るため従来同様トグル表示は維持し、アラートのみ表示する。
          if (amptalkAutoAssessmentSetting?.active === false) {
            this.hasAmptalkIntegrationError = true
          }
        },
        error: (error) => {
          console.error('Failed to initialize integrations:', error)
          captureException(error)
          this.isLoading = false
        },
      })
  }

  ngOnDestroy() {
    this.snackBar.dismiss()
  }

  onAmptalkToggleChange(event: MatSlideToggleChange) {
    if (event.checked) {
      this.openAmptalkIntegrationDialog()
    } else {
      this.openAmptalkResetWarningDialog()
    }
  }

  openAmptalkSettingDialog() {
    this.dialog.open<InboundSettingDialogComponent, InboundSettingDialogParam>(
      InboundSettingDialogComponent,
      {
        id: 'amptalk-setting-dialog',
        disableClose: true,
        panelClass: ['unset-mat-dialog-padding'],
        enterAnimationDuration: '0ms',
        data: {
          connectService: InboundConnectService.AmpTalk,
          enableWordFilter: false,
        },
      },
    )
  }

  openAmptalkIntegrationDialog() {
    this.dialog
      .open<AmptalkIntegrationComponent, AmptalkIntegrationData>(
        AmptalkIntegrationComponent,
        {
          disableClose: true,
          panelClass: 'unset-mat-dialog-padding',
          data: {
            apiKey: this.amptalkSetting.apiKey,
            authKey: this.amptalkSetting.authKey,
          },
        },
      )
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          if (!this.isAmptalkIntegrationEnabled) {
            this.isAmptalkIntegrationEnabled = false
          }
          return
        }
        this.isAmptalkIntegrationEnabled = true
        this.hasAmptalkIntegrationError = false
        this.amptalkSetting.apiKey = result.apiKey
        this.amptalkSetting.authKey = result.authKey
        this.dialog.open(IntegrationSuccessComponent, {
          data: { serviceName: 'amptalk' },
        })
      })
  }

  /** 最新設定に表示を合わせ、操作失敗からの自動再取得ではエラーを残す。 */
  private fetchAmazonConnectIntegrationSetting(
    preserveError = false,
  ): Observable<AmazonConnectIntegrationSetting | null> {
    this.hasAmazonConnectSettingLoaded = false
    return this.externalIntegrationService
      .getAmazonConnectIntegrationSetting()
      .pipe(
        tap((setting) => {
          this.amazonConnectSetting = setting
          this.syncAmazonConnectToggle()
          this.hasAmazonConnectSettingLoaded = true
          if (!preserveError) this.amazonConnectError = null
        }),
        catchError((error: unknown) => {
          captureException(error)
          this.hasAmazonConnectSettingLoaded = false
          const message =
            'Amazon Connect 連携設定の取得に失敗しました。時間をおいて再度お試しください。'
          this.amazonConnectError =
            preserveError && this.amazonConnectError != null
              ? `${this.amazonConnectError} ${message}`
              : message
          return of(null)
        }),
      )
  }

  /** 設定を前提とする操作の共通ガード。 */
  private get isAmazonConnectActionBlocked(): boolean {
    return (
      !this.isAmazonConnectFeatureEnabled ||
      this.isAmazonConnectProcessing ||
      !this.hasAmazonConnectSettingLoaded
    )
  }

  /** 保存済み設定にトグルを同期する。 */
  private syncAmazonConnectToggle(): void {
    this.isAmazonConnectIntegrationEnabled =
      this.amazonConnectSetting?.active === true
    if (this.isAmazonConnectIntegrationEnabled)
      this.hasAmazonConnectDisconnected = false
  }

  /** 設定の取得を再試行し、成功したらトグルと設定操作を再開できるようにする。 */
  reloadAmazonConnectIntegrationSetting(): void {
    // 取得失敗からの再試行なので loaded は条件に含めない。
    if (!this.isAmazonConnectFeatureEnabled || this.isAmazonConnectProcessing)
      return
    this.amazonConnectError = null
    this.isAmazonConnectProcessing = true
    this.fetchAmazonConnectIntegrationSetting()
      .pipe(
        finalize(() => {
          this.isAmazonConnectProcessing = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  /** 最新設定を取得してキーを残したまま受付を切り替える。未設定なら設定画面を開く。 */
  onAmazonConnectToggleChange(event: MatSlideToggleChange): void {
    if (this.isAmazonConnectActionBlocked) {
      this.syncAmazonConnectToggle()
      return
    }
    const setting = this.amazonConnectSetting
    if (setting == null || !setting.skillMapId) {
      if (event.checked) this.openAmazonConnectIntegrationDialog()
      else this.syncAmazonConnectToggle()
      return
    }
    this.amazonConnectError = null
    this.isAmazonConnectProcessing = true
    this.externalIntegrationService
      .getAmazonConnectIntegrationSetting()
      .pipe(
        switchMap((latestSetting) => {
          this.amazonConnectSetting = latestSetting
          this.syncAmazonConnectToggle()
          if (latestSetting == null || !latestSetting.skillMapId)
            throw new AmazonConnectOperationError(
              'BAD_REQUEST',
              '先に Amazon Connect 連携設定を保存してください',
            )
          if (event.checked && latestSetting.allowedCidrs.length === 0) {
            this.isAmazonConnectIntegrationEnabled = false
            this.amazonConnectError =
              '送信元 CIDR が未登録のため受付を開始できません。「連携設定を更新」から登録してください。'
            return of(null)
          }
          return this.externalIntegrationService
            .saveAmazonConnectIntegrationSetting({
              active: event.checked,
              skillMapId: latestSetting.skillMapId,
              allowedCidrs: [...latestSetting.allowedCidrs],
            })
            .pipe(switchMap(() => this.fetchAmazonConnectIntegrationSetting()))
        }),
        catchError((error: unknown) => {
          captureException(error)
          this.syncAmazonConnectToggle()
          this.amazonConnectError =
            error instanceof AmazonConnectOperationError
              ? error.message
              : 'Amazon Connect の取り込み受付の変更に失敗しました。時間をおいて再度お試しください。'
          return of(null)
        }),
        finalize(() => {
          this.isAmazonConnectProcessing = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  /** 保存済みなら設定を再取得し、未保存なら元のトグル状態に戻す。 */
  openAmazonConnectIntegrationDialog(): void {
    if (this.isAmazonConnectActionBlocked) return
    this.amazonConnectError = null
    this.isAmazonConnectProcessing = true
    this.dialog
      .open<
        AmazonConnectIntegrationComponent,
        AmazonConnectIntegrationData,
        AmazonConnectIntegrationDialogResult
      >(AmazonConnectIntegrationComponent, {
        disableClose: true,
        panelClass: ['unset-mat-dialog-padding'],
        data: { setting: this.amazonConnectSetting },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result?.saved === true)
            return this.fetchAmazonConnectIntegrationSetting()
          this.syncAmazonConnectToggle()
          return of(null)
        }),
        finalize(() => {
          this.isAmazonConnectProcessing = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  /** 設定が削除・無効化されても残存キーを管理できる。 */
  openInboundApiKeyManagementDialog(): void {
    // 設定を取得できなくても緊急失効できるよう loaded は条件に含めない。
    if (!this.isAmazonConnectFeatureEnabled || this.isAmazonConnectProcessing)
      return
    this.dialog.open<
      InboundApiKeyManagementComponent,
      InboundApiKeyManagementData
    >(InboundApiKeyManagementComponent, {
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
      maxWidth: '95vw',
      data: { intakeActive: this.amazonConnectSetting?.active === true },
    })
  }

  /** 解除確認後に全キーを失効し、失敗時は設定を再取得して再試行に備える。 */
  openAmazonConnectDisconnectWarningDialog(): void {
    if (
      this.isAmazonConnectActionBlocked ||
      this.amazonConnectSetting == null ||
      this.hasAmazonConnectDisconnected
    )
      return
    this.amazonConnectError = null
    this.isAmazonConnectProcessing = true
    this.dialog
      .open<AmazonConnectDisconnectWarningComponent, undefined, 'disconnect'>(
        AmazonConnectDisconnectWarningComponent,
        {
          disableClose: true,
          panelClass: ['unset-mat-dialog-padding'],
        },
      )
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result !== 'disconnect') {
            this.syncAmazonConnectToggle()
            return of(null)
          }
          return this.externalIntegrationService.disconnectAmazonConnect().pipe(
            tap(() => {
              this.hasAmazonConnectDisconnected = true
              this.isAmazonConnectIntegrationEnabled = false
              if (this.amazonConnectSetting != null)
                this.amazonConnectSetting = {
                  ...this.amazonConnectSetting,
                  active: false,
                }
            }),
            catchError((error: unknown) => {
              captureException(error)
              this.syncAmazonConnectToggle()
              this.amazonConnectError =
                'Amazon Connect 連携の解除に失敗しました。時間をおいて再度お試しください。'
              // キーの失効だけがコミットされている可能性があるため、最新の設定を確認する。
              return this.fetchAmazonConnectIntegrationSetting(true)
            }),
          )
        }),
        finalize(() => {
          this.isAmazonConnectProcessing = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  onKaikuraToggleChange(event: MatSlideToggleChange) {
    if (event.checked) {
      this.openKaikuraIntegrationDialog()
    } else {
      this.openKaikuraResetWarningDialog()
    }
  }

  openKaikuraIntegrationDialog() {
    this.dialog
      .open<KaikuraIntegrationComponent, KaikuraIntegrationData>(
        KaikuraIntegrationComponent,
        {
          disableClose: true,
          panelClass: ['unset-mat-dialog-padding'],
          data: {
            setting: this.kaikuraSetting,
          },
        },
      )
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result?: KaikuraIntegrationResult) => {
        if (!result) {
          this.isKaikuraIntegrationEnabled =
            this.kaikuraSetting?.active === true
          return
        }
        this.isKaikuraIntegrationEnabled = true
        this.kaikuraSetting = result
        this.dialog.open(IntegrationSuccessComponent, {
          data: { serviceName: 'カイクラ' },
        })
      })
  }

  onBlueBeanToggleChange(event: MatSlideToggleChange) {
    if (event.checked) {
      this.openBlueBeanIntegrationDialog()
    } else {
      this.openBlueBeanResetWarningDialog()
    }
  }

  openBlueBeanIntegrationDialog() {
    this.dialog
      .open<BlueBeanIntegrationComponent, BlueBeanIntegrationData>(
        BlueBeanIntegrationComponent,
        {
          disableClose: true,
          panelClass: ['unset-mat-dialog-padding'],
          data: {
            setting: this.blueBeanSetting,
          },
        },
      )
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result?: BlueBeanIntegrationResult) => {
        if (!result) {
          this.isBlueBeanIntegrationEnabled =
            this.blueBeanSetting?.active === true
          return
        }
        this.isBlueBeanIntegrationEnabled = true
        this.blueBeanSetting = result
      })
  }

  openBlueBeanOperatorMappingDialog() {
    // 部品が自前で fetch するため data は渡さない。背景クリック / ESC での
    // 不用意な閉じを防ぐため disableClose を付ける (兄弟の連携ダイアログと一貫)。
    // 明示的な閉じはヘッダーの × ボタンのみ。
    this.dialog.open(BlueBeanOperatorMappingDialogComponent, {
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
    })
  }

  openBlueBeanResetWarningDialog() {
    this.dialog
      .open(IntegrationResetWarningComponent, {
        disableClose: true,
        panelClass: ['unset-mat-dialog-padding'],
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result === 'reset') {
            return this.externalIntegrationService
              .disconnectBlueBeanIntegration()
              .pipe(
                tap(() => {
                  this.initializeBlueBeanIntegration()
                }),
                catchError((error) => {
                  this.isBlueBeanIntegrationEnabled = true
                  captureException(error)
                  return of(null)
                }),
              )
          } else {
            this.isBlueBeanIntegrationEnabled = true
            return of(null)
          }
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  private initializeBlueBeanIntegration() {
    this.isBlueBeanIntegrationEnabled = false
    this.blueBeanSetting = null
  }

  onBiztelToggleChange(event: MatSlideToggleChange) {
    if (event.checked) {
      this.openBiztelIntegrationDialog()
    } else {
      this.openBiztelResetWarningDialog()
    }
  }

  openBiztelIntegrationDialog() {
    this.dialog
      .open<BiztelIntegrationComponent, BiztelIntegrationData>(
        BiztelIntegrationComponent,
        {
          disableClose: true,
          panelClass: ['unset-mat-dialog-padding'],
          data: {
            setting: this.biztelSetting,
          },
        },
      )
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result?: BiztelIntegrationResult) => {
        if (!result) {
          this.isBiztelIntegrationEnabled = this.biztelSetting?.active === true
          return
        }
        // 保存成功通知は設定ダイアログ側の snackBar が担うため、ここでは
        // 成功ダイアログを開かない (BlueBean と同型)。
        this.isBiztelIntegrationEnabled = true
        this.biztelSetting = result
      })
  }

  openBiztelAccountMappingDialog() {
    // 部品が自前で fetch するため data は渡さない。背景クリック / ESC での
    // 不用意な閉じを防ぐため disableClose を付ける (兄弟の連携ダイアログと一貫)。
    // 明示的な閉じはヘッダーの × ボタンのみ。
    this.dialog.open(BiztelAccountMappingDialogComponent, {
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
    })
  }

  openBiztelPhoneNumberTagMappingDialog() {
    // 番号 → 商談タグ のマッピング設定。部品が自前で fetch するため data は
    // 渡さない (account-mapping と同型)。
    this.dialog.open(BiztelPhoneNumberTagMappingDialogComponent, {
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
    })
  }

  openBiztelResetWarningDialog() {
    this.dialog
      .open(IntegrationResetWarningComponent, {
        disableClose: true,
        panelClass: ['unset-mat-dialog-padding'],
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result === 'reset') {
            return this.externalIntegrationService
              .disconnectBiztelIntegration()
              .pipe(
                tap(() => {
                  this.initializeBiztelIntegration()
                }),
                catchError((error) => {
                  this.isBiztelIntegrationEnabled = true
                  captureException(error)
                  return of(null)
                }),
              )
          } else {
            this.isBiztelIntegrationEnabled = true
            return of(null)
          }
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  private initializeBiztelIntegration() {
    this.isBiztelIntegrationEnabled = false
    this.biztelSetting = null
  }

  validateSalesforceIntegrationSetting() {
    this.isLoading = true
    this.externalIntegrationService
      .validateSalesforceIntegrationSetting()
      .pipe(
        tap((result) => {
          this.isLoading = false
          if (!result.isValid) {
            this.alreadyValidatedSalesforceIntegration = false
            this.dialog.open<
              SalesforceIntegrationValidationErrorComponent,
              SalesforceIntegrationValidationErrorParam
            >(SalesforceIntegrationValidationErrorComponent, {
              data: { salesforceValidationResult: result },
            })
          } else {
            this.alreadyValidatedSalesforceIntegration = true
            this.dialog.open(SalesforceIntegrationValidationSuccessComponent)
          }
        }),
        catchError((error) => {
          this.isLoading = false
          this.snackBar.open(
            'Salesforceの連携設定の検証に失敗しました。',
            '閉じる',
          )
          captureException(error)
          return of(null)
        }),
      )
      .subscribe()
  }

  openOutboundSettingDialog() {
    this.dialog.open(OutboundSettingDialogComponent, {
      id: 'outbound-setting-dialog',
      disableClose: true,
      panelClass: ['unset-mat-dialog-padding'],
      enterAnimationDuration: '0ms',
    })
  }

  private openAmptalkResetWarningDialog() {
    this.dialog
      .open(IntegrationResetWarningComponent, {
        disableClose: true,
        panelClass: ['unset-mat-dialog-padding'],
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result === 'reset') {
            return this.externalIntegrationService.disconnectAmptalk().pipe(
              tap(() => {
                this.isAmptalkIntegrationEnabled = false
                this.initializeAmptalkIntegration()
              }),
              catchError((error) => {
                this.isAmptalkIntegrationEnabled = true
                captureException(error)
                return of(null)
              }),
            )
          } else {
            this.isAmptalkIntegrationEnabled = true
            return of(null)
          }
        }),
      )
      .subscribe()
  }

  private initializeAmptalkIntegration() {
    this.isAmptalkIntegrationEnabled = false
    this.hasAmptalkIntegrationError = false
    this.amptalkSetting = {
      apiKey: '',
      authKey: '',
    }
  }

  openKaikuraResetWarningDialog() {
    this.dialog
      .open(IntegrationResetWarningComponent, {
        disableClose: true,
        panelClass: ['unset-mat-dialog-padding'],
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result === 'reset') {
            return this.externalIntegrationService.disconnectKaikura().pipe(
              tap(() => {
                this.initializeKaikuraIntegration()
              }),
              catchError((error) => {
                this.isKaikuraIntegrationEnabled = true
                captureException(error)
                return of(null)
              }),
            )
          } else {
            this.isKaikuraIntegrationEnabled = true
            return of(null)
          }
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  private initializeKaikuraIntegration() {
    this.isKaikuraIntegrationEnabled = false
    this.kaikuraSetting = null
  }

  async onZoomToggleChange(event: MatSlideToggleChange) {
    if (event.checked) {
      this.isZoomIntegrationEnabled = true
      const code = await this.openZoomAuthPopup()

      if (code == null) {
        // codeが取得できなかった場合のエラーハンドリング
        this.isZoomIntegrationEnabled = false
        this.snackBar.open('Zoom認証がキャンセルされました。', '閉じる')
        return
      }

      this.externalIntegrationService
        .saveZoomToken(code)
        .pipe(
          tap(() => {
            this.isZoomIntegrationEnabled = true
            this.hasZoomIntegrationError = false
            this.dialog.open(IntegrationSuccessComponent, {
              data: { serviceName: 'zoom' },
            })
          }),
          catchError((error) => {
            this.isZoomIntegrationEnabled = false
            this.snackBar.open('Zoomの連携に失敗しました。', '閉じる')
            captureException(error)
            return of(null)
          }),
        )
        .subscribe()
    } else {
      this.openZoomResetWarningDialog()
    }
  }

  openZoomSettingDialog() {
    this.dialog.open<InboundSettingDialogComponent, InboundSettingDialogParam>(
      InboundSettingDialogComponent,
      {
        id: 'zoom-setting-dialog',
        disableClose: true,
        panelClass: ['unset-mat-dialog-padding'],
        enterAnimationDuration: '0ms',
        data: {
          connectService: InboundConnectService.Zoom,
          enableWordFilter: true,
        },
      },
    )
  }

  async onSalesforceToggleChange(event: MatSlideToggleChange) {
    if (event.checked) {
      this.openSalesforceIntegrationDialog()
    } else {
      this.openSalesforceResetWarningDialog()
    }
  }

  private async openSalesforceIntegrationDialog() {
    const code = await this.salesforceAuthService.requestCode({
      clientId: environment.sfOAuth.clientId,
      redirectUri: environment.sfOAuth.redirectUri,
      scope: environment.sfOAuth.scope,
    })

    if (!code) {
      this.isSalesforceIntegrationEnabled = false
      return
    }

    this.externalIntegrationService
      .saveSalesforceToken(code)
      .pipe(
        tap(() => {
          this.isSalesforceIntegrationEnabled = true
          this.hasSalesforceIntegrationError = false
          this.dialog.open(SalesforceConnectSuccessComponent)
        }),
        catchError((error) => {
          this.isSalesforceIntegrationEnabled = false
          this.snackBar.open('Salesforceの連携に失敗しました。', '閉じる')
          captureException(error)
          return of(null)
        }),
      )
      .subscribe()
  }

  private openZoomAuthPopup(): Promise<string | null> {
    const clientId = environment.zoomClientId
    const redirectUri = environment.baseUrl + 'admin/external-integration'

    const zoomAuthUrl = `https://zoom.us/oauth/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}`

    return new Promise<string | null>((resolve) => {
      const popup = window.open(zoomAuthUrl, '_blank', 'width=600,height=600')

      const timer = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(timer)
          return resolve(null)
        }

        if (popup.location.href.includes('/external-integration')) {
          const url = new URL(popup.location.href)
          const code = url.searchParams.get('code')

          popup.close()
          clearInterval(timer)
          return resolve(code)
        }
      }, 1000)
    })
  }

  private openZoomResetWarningDialog() {
    this.dialog
      .open(IntegrationResetWarningComponent, {
        disableClose: true,
        panelClass: 'unset-mat-dialog-padding',
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result === 'reset') {
            return this.externalIntegrationService.disconnectZoom().pipe(
              tap(() => {
                this.isZoomIntegrationEnabled = false
                this.initializeZoomIntegration()
              }),
              catchError((error) => {
                this.isZoomIntegrationEnabled = true
                captureException(error)
                return of(null)
              }),
            )
          } else {
            this.isZoomIntegrationEnabled = true
            return of(null)
          }
        }),
      )
      .subscribe()
  }

  private initializeZoomIntegration() {
    this.isZoomIntegrationEnabled = false
    this.hasZoomIntegrationError = false
  }

  private openSalesforceResetWarningDialog() {
    this.dialog
      .open(IntegrationResetWarningComponent, {
        disableClose: true,
        panelClass: 'unset-mat-dialog-padding',
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result === 'reset') {
            return this.externalIntegrationService.disconnectSalesforce().pipe(
              tap(() => {
                this.isSalesforceIntegrationEnabled = false
                this.initializeSalesforceIntegration()
              }),
              catchError((error) => {
                this.isSalesforceIntegrationEnabled = true
                captureException(error)
                return of(null)
              }),
            )
          } else {
            this.isSalesforceIntegrationEnabled = true
            return of(null)
          }
        }),
      )
      .subscribe()
  }

  private initializeSalesforceIntegration() {
    this.isSalesforceIntegrationEnabled = false
    this.hasSalesforceIntegrationError = false
    this.alreadyValidatedSalesforceIntegration = false
  }
}
